<script setup lang="ts">
/**
 * A mailbox's signature (#1115), the Signature tab of the mailbox panel: written in the same editor as a mail, saved, or
 * removed. The OS adds it to every mail the mailbox sends, so nobody has to sign off by hand. Everything goes through
 * `signature`, so the same tab keeps a person's own and, for an owner, a member's.
 *
 * When the enterprise has a signature template (#1118), the tab shows it filled in for this mailbox, says when it is
 * the one in use (no signature of the mailbox's own), and offers a switch to use it or not.
 *
 * Below, each of the mailbox's extra addresses (#1122), when the source lists them, with its own signature: what mail
 * that leaves from that address carries in place of the mailbox's.
 */
import { Button, Label, Switch } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import type { MailAliasRow, MailAliasSignatureSave, MailSignature } from '@/api'
import MailAddressSignature from '@/components/MailAddressSignature.vue'
import type { MailboxSignature } from '@/components/MailboxPanel.vue'
import MailEditor from '@/components/MailEditor.vue'
import { draftIsBlank, draftOfText, emptyDraft, type MailDraft } from '@/lib/mailDraft'
import { hasWords, signatureInUse } from '@/lib/mailSignature'

const props = withDefaults(defineProps<{ signature: MailboxSignature; own?: boolean }>(), { own: false })
/** The signature as it now stands, after a save or a removal, so the panel's writing forms show it. */
const emit = defineEmits<{ saved: [signature: MailSignature]; addresses: [addresses: MailAliasRow[]] }>()

const draft = ref<MailDraft>(emptyDraft())
const saved = ref<MailSignature | null>(null)
const loading = ref(true)
const busy = ref(false)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)

/** `lead`, and the server's sentence after it when the refusal carried one: a bare code adds nothing. */
function failureText(caught: unknown, lead: string): string {
    const said = caught instanceof Error ? caught.message.trim() : ''
    return /\s/.test(said) ? `${lead} ${said}` : lead
}

/** What the editor opens with: the signature's HTML, or its text laid out as paragraphs for one written as text. */
function draftOf(signature: MailSignature): MailDraft {
    return signature.html === '' ? draftOfText(signature.text) : { html: signature.html, text: signature.text }
}

/** The enterprise's template filled in for this mailbox, when the enterprise has one. */
const template = computed(() => (saved.value?.template && hasWords(saved.value.template) ? saved.value.template : null))
/** Whether the template is what this mailbox's mail is signed with now. */
const signedByTemplate = computed(() => signatureInUse(saved.value)?.fromTemplate === true)

function show(signature: MailSignature): void {
    saved.value = signature
    draft.value = draftOf(signature)
    emit('saved', signature)
}

async function load(): Promise<void> {
    loading.value = true
    try {
        const signature = await props.signature.read()
        saved.value = signature
        draft.value = draftOf(signature)
    } catch {
        error.value = 'Could not load the signature.'
    } finally {
        loading.value = false
    }
    await loadAddresses()
}

/** The mailbox's extra addresses and their own signatures (#1122), where the source lists them. */
const addresses = ref<MailAliasRow[]>([])

async function loadAddresses(): Promise<void> {
    const source = props.signature.addresses
    if (!source) return
    try {
        addresses.value = await source.list()
    } catch {
        error.value = 'Could not load the signatures of your other addresses.'
    }
}

/**
 * An address's signature saved (`body`) or removed (none): the list as it now stands, shown here and handed to the
 * panel. A refusal is thrown on, so the address's editor stays open and says what went wrong.
 */
async function changeAddress(address: MailAliasRow, body?: MailAliasSignatureSave): Promise<void> {
    const source = props.signature.addresses
    if (!source) return
    addresses.value = body ? await source.save(address.id, body) : await source.clear(address.id)
    emit('addresses', addresses.value)
    notice.value = null
}

async function save(): Promise<void> {
    if (busy.value || draftIsBlank(draft.value)) return
    busy.value = true
    error.value = null
    try {
        show(await props.signature.save({ html: draft.value.html, text: draft.value.text }))
        notice.value = 'Signature saved.'
    } catch (caught) {
        error.value = failureText(caught, 'Could not save the signature.')
    } finally {
        busy.value = false
    }
}

/** Switches the enterprise's template on or off for this mailbox; its own signature, and the draft, stay as they are. */
async function useTemplate(on: boolean): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = null
    try {
        saved.value = await props.signature.save({ useTemplate: on })
        emit('saved', saved.value)
        notice.value = on ? 'The enterprise signature is used.' : 'The enterprise signature is not used.'
    } catch (caught) {
        error.value = failureText(caught, 'Could not change the enterprise signature.')
    } finally {
        busy.value = false
    }
}

async function remove(): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = null
    try {
        show(await props.signature.clear())
        notice.value = 'Signature removed.'
    } catch (caught) {
        error.value = failureText(caught, 'Could not remove the signature.')
    } finally {
        busy.value = false
    }
}

onMounted(() => void load())
</script>

<template>
	<form class="space-y-3" data-testid="mail-signature" @submit.prevent="save">
		<p class="text-muted-foreground text-sm">
			{{
				own
					? 'Added after every mail you send, reply and forward, from here, the API or MCP. Leave it off one mail under its editor.'
					: 'Added after every mail this member sends: its tasks’ mail, signed before it waits for approval, and yours.'
			}}
		</p>
		<p v-if="loading" class="text-muted-foreground text-sm">Loading the signature…</p>
		<template v-else>
			<MailEditor v-model="draft" label="Signature" placeholder="Your name, title and a link" :disabled="busy" testid="mail-signature-editor" />
			<div class="flex flex-wrap gap-2">
				<Button type="submit" size="sm" data-testid="mail-signature-save" :disabled="busy || draftIsBlank(draft)">Save signature</Button>
				<Button v-if="hasWords(saved)" type="button" size="sm" variant="outline" data-testid="mail-signature-remove" :disabled="busy" @click="remove">
					Remove signature
				</Button>
			</div>
			<div v-if="template" class="border-border space-y-2 border-t pt-3" data-testid="mail-signature-template">
				<div class="flex items-start justify-between gap-4 text-sm">
					<div class="space-y-1">
						<Label for="mail-signature-use-template">Use the enterprise signature</Label>
						<p class="text-muted-foreground text-xs">
							{{
								own
									? 'An owner set one signature for every mailbox. It signs your mail while you have no signature of your own.'
									: 'An owner set one signature for every mailbox. It signs this member’s mail while it has no signature of its own.'
							}}
						</p>
					</div>
					<Switch
						id="mail-signature-use-template"
						:model-value="saved?.useTemplate !== false"
						data-testid="mail-signature-use-template"
						aria-label="Use the enterprise signature"
						:disabled="busy"
						@update:model-value="useTemplate($event === true)"
					/>
				</div>
				<p v-if="signedByTemplate" class="text-sm" data-testid="mail-signature-template-in-use">
					{{ own ? 'Your mail is signed with the enterprise signature:' : 'This member’s mail is signed with the enterprise signature:' }}
				</p>
				<pre class="text-muted-foreground border-border border-l-2 pl-3 font-sans text-xs whitespace-pre-wrap" data-testid="mail-signature-template-preview">{{ template.text }}</pre>
			</div>
		</template>
		<div v-if="signature.addresses && addresses.length > 0" class="border-border space-y-3 border-t pt-3" data-testid="mail-signature-addresses">
			<div class="space-y-1">
				<p class="text-sm font-medium">{{ own ? 'Your other addresses' : 'This member’s other addresses' }}</p>
				<p class="text-muted-foreground text-xs">Mail that leaves from one of these addresses carries its own signature, when it has one, in place of the signature above.</p>
			</div>
			<ul class="space-y-3">
				<li v-for="address in addresses" :key="address.id" class="space-y-1">
					<span class="font-mono text-sm">{{ address.address }}</span>
					<MailAddressSignature
						:alias="address"
						editable
						:save="body => changeAddress(address, body)"
						:clear="() => changeAddress(address)"
					/>
				</li>
			</ul>
		</div>
		<p v-if="notice" class="text-sm" data-testid="mail-signature-notice" aria-live="polite">{{ notice }}</p>
		<p v-if="error" class="text-destructive text-sm" data-testid="mail-signature-error">{{ error }}</p>
	</form>
</template>
